import { useState, useEffect, useCallback } from 'react';
import { fetchOperations } from './requester';
import { Operation, OperationsResponse } from './types';
import { mergeOperations } from '../../utils/operations';
import { updateSelectedOperationsFromSessionStorage } from '../../utils/operationsInSessionStorage';
import { ALL_OPERATIONS, OperationStorage } from '../../constants/operations';

export const useOperations = (userId: string): {
  isLoading: boolean,
  operations: Operation[],
  operationsSelected: Operation[]
  setSelectedOperations: (operationSelected: Operation[]) => void,
} => {
  const [operations, setOperations] = useState<Operation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [operationsSelected, setOperationsSelected] = useState([]);
  const removeAllOperationsItem = ({ operationId }: { operationId: string }) => operationId !== ALL_OPERATIONS;


  const getOperations = useCallback(async () => {
    if (userId) {
      try {
        const operationsResponse: OperationsResponse = await fetchOperations(userId);
        const mergeResponse = mergeOperations(operationsResponse);
        setOperations(mergeResponse);
        updateSelectedOperationsFromSessionStorage(mergeResponse, setOperationsSelected);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    }
  }, [userId]);

  useEffect(() => {
    getOperations();
  }, [getOperations]);

  const handleAllOperationsSelection = (userHasOneOperation: boolean, allOperationsWithOutAllId: Operation[], selected: Operation[]) => {
    if (!userHasOneOperation) {
      const allOperationIds = allOperationsWithOutAllId.map(({ operationId }) => operationId);
      sessionStorage.setItem(OperationStorage.OperationsSelected, JSON.stringify(allOperationIds));
      sessionStorage.setItem(OperationStorage.OperationSelected, ALL_OPERATIONS);
      setOperationsSelected(selected);
    } else {
      sessionStorage.setItem(OperationStorage.OperationSelected, operations[1].operationId);
      sessionStorage.setItem(OperationStorage.OperationsSelected, JSON.stringify([operations[1].operationId]));
      setOperationsSelected([operations[1]]);
    }
  };

  const handleNormalSelection = (selected: Operation[]) => {
    const getOperationsId = selected.map(({ operationId }) => operationId);
    sessionStorage.setItem(OperationStorage.OperationSelected, getOperationsId.length ? getOperationsId[0] : operations[0].operationId);
    sessionStorage.setItem(OperationStorage.OperationsSelected, JSON.stringify(getOperationsId));
    setOperationsSelected(selected);
  };

  const setSelectedOperations = (selected: Operation[]) => {
    if (!Array.isArray(selected)) return;

    const allOperationsObjectSelected = selected[0].operationId === ALL_OPERATIONS;
    const allOperationsWithOutAllId = operations.filter(removeAllOperationsItem);
    const allOperationsAreSelected = selected.length === allOperationsWithOutAllId.length;
    const userHasOneOperation = operations.length === 2;

    if (allOperationsObjectSelected || allOperationsAreSelected) {
      handleAllOperationsSelection(userHasOneOperation, allOperationsWithOutAllId, selected);
    } else {
      handleNormalSelection(selected);
    }
  };

  return {
    isLoading,
    operations,
    operationsSelected,
    setSelectedOperations
  };
};
